import { useAtom } from "jotai";
import _ from "lodash";
import { useEffect, useMemo } from "react";
import { generateInitRowKeys } from "../../../../utils";
import { AvisEditableListComponent } from "./";
import { atomRowKeys } from "./atoms";
import { IAvisEditableListProps, IRowState } from "./types";

export function AvisEditableListJotaiHoc<T extends object, P extends object>(
  props: Readonly<IAvisEditableListProps<T, P>>,
): JSX.Element {
  const { bag, bagField } = props;

  const [rowKeys, setRowKeys] = useAtom(atomRowKeys);

  if (!Array.isArray(bag.values[bagField]))
    throw new TypeError("Editable list bagField must be array of objects");

  const bagFieldArray = useMemo(
    () => bag.values[bagField] as P[],
    [bag, bagField],
  );

  useEffect(() => {
    if (rowKeys || !bagFieldArray) return;

    setRowKeys(generateInitRowKeys(bagFieldArray));
  }, [rowKeys, bagFieldArray, setRowKeys]);

  useEffect(() => {
    if (!rowKeys || bagFieldArray.length === rowKeys.length) return;

    //Adding items externally causes missing rowKeys
    //If there are missing keys - add keys to state
    if (bagFieldArray.length > rowKeys.length) {
      const missingKeys = bagFieldArray.length - rowKeys.length;
      const appendKeys = [...Array(missingKeys).keys()].map(() => _.uniqueId());
      setRowKeys((oldArray) =>
        oldArray ? [...oldArray, ...appendKeys] : [...appendKeys],
      );
    }
  }, [bagFieldArray, rowKeys, bagField, setRowKeys]);

  const rowStatesJSON = useMemo(
    () =>
      JSON.stringify(
        ((bag.values?.[bagField] as Array<IRowState<P>>) ?? [])
          .filter((row) => row.rowState !== "Add")
          .map((row) => {
            return { rowState: row.rowState };
          }),
      ),
    [bag.values, bagField],
  );
  if (!Array.isArray(bag.values[bagField]))
    throw new TypeError("Editable list bagField must be array of objects");

  return (
    <AvisEditableListComponent
      forceUpdateKey={JSON.stringify(rowStatesJSON)}
      {...props}
    />
  );
}
